import { DatePicker } from "@material-ui/pickers";
import {
  ArrayInput,
  BooleanInput,
  Button,
  ButtonProps,
  FormDataConsumer,
  FormTab,
  ReferenceInput,
  required,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextInput,
  useTranslate,
} from "react-admin";
import { useForm } from "react-final-form";
import { GenericDateOrTimePicker } from "../../common/form/GenericDateOrTimePicker";
import AddCircleOutlineRoundedIcon from "@material-ui/icons/AddCircleOutlineRounded";
import RemoveCircleOutlineRoundedIcon from "@material-ui/icons/RemoveCircleOutlineRounded";
import "./ApiClientForm.css";
import { getCryptoRandomString } from "../../../common/util/randomString";
import resourceNames from "../../../common/data/resourceNames";

interface Props {
  initialClientId?: string;
  isEdit?: boolean;
}

const AddItemButton = ({ onClick, ...rest }: ButtonProps) => {
  const { change, getFieldState } = useForm();
  return (
    <Button
      label="ra.action.add"
      {...rest}
      onClick={(e) => {
        const fieldState = getFieldState("secrets");
        const nextIndex =
          fieldState?.value && Array.isArray(fieldState.value)
            ? fieldState.value.length
            : 0;
        onClick && onClick(e);
        change(`secrets[${nextIndex}]`, {
          value: getCryptoRandomString(32),
          description: "",
          isExisting: false,
          expiration: null,
        });
      }}
    >
      <AddCircleOutlineRoundedIcon />
    </Button>
  );
};

const RemoveItemButton = ({ onClick, ...rest }: ButtonProps) => {
  const {
    getFieldState,
    mutators: { push },
  } = useForm();
  return (
    <Button
      label="ra.action.remove"
      {...rest}
      onClick={(e) => {
        const fieldState = getFieldState("secrets");
        const index = rest.className
          ? /button-remove button-remove-secrets-(?<index>[0-9]+)/.exec(
              rest.className
            )?.groups?.index
          : undefined;
        if (!index) {
          console.error(
            "Unable to obtain the item index. Please notify maintainers."
          );
          return;
        }

        if (!fieldState || !Array.isArray(fieldState.value)) {
          onClick && onClick(e);
          return;
        }

        const removedSecret = fieldState.value[Number(index)];
        if (!removedSecret.isExisting) {
          onClick && onClick(e);
          return;
        }

        push("deletedSecrets", {
          value: fieldState.value[Number(index)].value,
        });
        onClick && onClick(e);
      }}
    >
      <RemoveCircleOutlineRoundedIcon />
    </Button>
  );
};

export const ApiClientForm = ({ initialClientId, isEdit, ...props }: Props) => {
  const translate = useTranslate();
  return (
    <TabbedForm className="form-create-edit" variant="outlined" {...props}>
      <FormTab label="resources.admin-api-clients.labels.basicDetails">
        {isEdit ? (
          <ArrayInput source="deletedSecrets" style={{ display: "none" }}>
            <SimpleFormIterator>
              <TextInput source="value" />
            </SimpleFormIterator>
          </ArrayInput>
        ) : null}
        <TextInput
          source="name"
          label="resources.admin-api-clients.labels.name"
          validate={required()}
        />
        <TextInput
          source="clientId"
          label="resources.admin-api-clients.labels.clientId"
          validate={required()}
          initialValue={initialClientId}
        />
        <ReferenceInput
          source="companyId"
          reference={resourceNames.ADMIN_COMPANIES}
        >
          <SelectInput />
        </ReferenceInput>
        <ArrayInput
          source="secrets"
          label="resources.admin-api-clients.labels.keys"
        >
          <SimpleFormIterator
            disableReordering
            addButton={<AddItemButton />}
            removeButton={<RemoveItemButton />}
          >
            <BooleanInput source="isExisting" style={{ display: "none" }} />
            <FormDataConsumer>
              {({ getSource, scopedFormData, formData, ...rest }) => (
                <TextInput
                  {...rest}
                  source={getSource ? getSource("value") : "value"}
                  label="resources.admin-api-clients.labels.value"
                  validate={required()}
                  type={scopedFormData?.isExisting ? "password" : "text"}
                  disabled={scopedFormData?.isExisting}
                  inputProps={{ readOnly: true }}
                  helperText={
                    scopedFormData?.isExisting
                      ? undefined
                      : translate(
                          "resources.admin-api-clients.labels.valueHelperText"
                        )
                  }
                />
              )}
            </FormDataConsumer>

            <TextInput
              source="description"
              label="resources.admin-api-clients.labels.description"
              validate={required()}
            />
            <GenericDateOrTimePicker
              PickerComponent={DatePicker}
              source="expiration"
              label="resources.common.labels.expirationDate"
              options={{
                autoOk: true,
                format: "MMMM do yyyy",
              }}
            />
          </SimpleFormIterator>
        </ArrayInput>
      </FormTab>
    </TabbedForm>
  );
};
